import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Plus, Search, Edit, Trash2, Loader2, Ticket, Percent, DollarSign, Calendar } from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

interface Cupon {
  id: string
  codigo: string
  descripcion: string
  tipo: 'percent' | 'fixed'
  valor: number
  minimo: number
  maxUsos: number | null
  usos: number
  validoDesde: string
  validoHasta: string
  activo: boolean
}

interface CuponesData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: Cupon[]
}

function tipoBadge(tipo: 'percent' | 'fixed') {
  if (tipo === 'percent') {
    return (
      <Badge variant="info">
        <Percent className="size-3" /> Porcentaje
      </Badge>
    )
  }
  return (
    <Badge variant="success">
      <DollarSign className="size-3" /> Fijo
    </Badge>
  )
}

function vigenciaBadge(validoHasta: string) {
  if (!validoHasta) return <span className="text-xs text-ink-400">Sin vencimiento</span>
  const hoy = new Date().toISOString().slice(0, 10)
  if (validoHasta < hoy) return <Badge variant="danger">Vencido</Badge>
  const dias = Math.ceil((new Date(validoHasta).getTime() - new Date(hoy).getTime()) / 86_400_000)
  if (dias <= 7) return <Badge variant="warning">Vence en {dias}d</Badge>
  return <span className="text-xs text-ink-600">Hasta {validoHasta}</span>
}

export function Cupones() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, isFetching } = useQuery<CuponesData>({
    queryKey: ['cupones', page, searchTerm],
    queryFn: () =>
      apiGet(`/api/admin/cupones?page=${page}${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''}`),
    placeholderData: (prev) => prev,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/admin/cupones/${id}`, { method: 'DELETE' })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Error' }))
        throw new Error(err.error || 'Error eliminando')
      }
      return r.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cupones'] }),
    onError: (e: Error) => alert(e.message),
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  function onDelete(id: string, codigo: string) {
    if (confirm(`¿Eliminar el cupón "${codigo}"?`)) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <>
      <Topbar
        title="Cupones"
        subtitle={data ? `${data.total.toLocaleString('es-MX')} cupones de descuento` : 'Cargando…'}
        actions={
          <button
            onClick={() => navigate({ to: '/cupones/nuevo' as any })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo
          </button>
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por código o descripción…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando cupones…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Código</th>
                      <th className="px-4 py-3 font-medium">Tipo</th>
                      <th className="px-4 py-3 font-medium text-right">Valor</th>
                      <th className="px-4 py-3 font-medium text-right">Mínimo</th>
                      <th className="px-4 py-3 font-medium">Usos</th>
                      <th className="px-4 py-3 font-medium">Vigencia</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="w-20 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-ink-500">
                          No hay cupones para mostrar
                        </td>
                      </tr>
                    )}
                    {data.data.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
                              <Ticket className="size-4" />
                            </div>
                            <div>
                              <div className="font-mono font-semibold text-ink-900">{c.codigo}</div>
                              {c.descripcion && (
                                <div className="text-xs text-ink-500 line-clamp-1">{c.descripcion}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">{tipoBadge(c.tipo)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-ink-900">
                          {c.tipo === 'percent' ? `${c.valor}%` : formatMXN(c.valor)}
                        </td>
                        <td className="px-4 py-3 text-right text-ink-700">
                          {c.minimo > 0 ? formatMXN(c.minimo) : <span className="text-ink-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-ink-700">
                          {c.usos}
                          {c.maxUsos ? <span className="text-ink-400"> / {c.maxUsos}</span> : ''}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="size-3 text-ink-400" />
                            {vigenciaBadge(c.validoHasta)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {c.activo ? (
                            <Badge variant="success">Activo</Badge>
                          ) : (
                            <Badge variant="default">Inactivo</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <button
                              onClick={() => navigate({ to: `/cupones/${c.id}` as any })}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                              title="Editar"
                            >
                              <Edit className="size-4" />
                            </button>
                            <button
                              onClick={() => onDelete(c.id, c.codigo)}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700"
                              title="Eliminar"
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong> (
                  {data.total.toLocaleString('es-MX')} cupones)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
